import type { RemoteVideoTrack } from 'livekit-client';
import type { RtpSnapshot } from '../core/video-stats.js';

/**
 * Pull one snapshot of the inbound screen-share RTP stats out of the browser.
 *
 * We go to `getRTCStatsReport()` rather than the SDK's `getReceiverStats()` because the latter drops
 * exactly the fields that diagnose a quality complaint: framesPerSecond, jitterBufferDelay,
 * freezeCount and qpSum (the quantizer — the actual measure of "blurry").
 */
export async function readVideoStats(track: RemoteVideoTrack): Promise<RtpSnapshot | null> {
  const report = await track.getRTCStatsReport().catch(() => undefined);
  if (!report) return null;

  let inbound: Record<string, number | string> | null = null;
  let rttMs: number | null = null;

  report.forEach((entry: Record<string, unknown>) => {
    const type = entry.type as string | undefined;
    if (type === 'inbound-rtp' && entry.kind === 'video') {
      inbound = entry as Record<string, number | string>;
    } else if (type === 'candidate-pair' && (entry.nominated === true || entry.state === 'succeeded')) {
      const rtt = entry.currentRoundTripTime as number | undefined;
      if (typeof rtt === 'number') rttMs = Math.round(rtt * 1000);
    }
  });
  if (!inbound) return null;
  const rtp = inbound as Record<string, number | string>;

  const num = (k: string): number => (typeof rtp[k] === 'number' ? (rtp[k] as number) : 0);
  const codecEntry = typeof rtp.codecId === 'string'
    ? (report.get(rtp.codecId as string) as { mimeType?: string } | undefined)
    : undefined;

  return {
    ts: num('timestamp') || Date.now(),
    bytesReceived: num('bytesReceived'),
    framesDecoded: num('framesDecoded'),
    qpSum: num('qpSum'),
    jitterBufferDelay: num('jitterBufferDelay'),
    jitterBufferEmittedCount: num('jitterBufferEmittedCount'),
    packetsReceived: num('packetsReceived'),
    packetsLost: num('packetsLost'),
    freezeCount: num('freezeCount'),
    pliCount: num('pliCount'),
    frameWidth: num('frameWidth'),
    frameHeight: num('frameHeight'),
    framesPerSecond: num('framesPerSecond'),
    codec: (codecEntry?.mimeType ?? '').replace('video/', '') || '—',
    decoder: (rtp.decoderImplementation as string) ?? '—',
    rttMs,
  };
}
